import React from 'react';
import '../../assets/css/case-property.css';
import cornerIcon from '../../assets/images/case-image-corner.svg';
// @ts-ignore
import { useProperty, useContentToken, EditableToken, RichTextContent } from '@vibesites/runtime/react';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { getPropertyBasePath } from './propertyUtils';
import { openTourScheduler } from './TourSchedulerModal';

/**
 * Property About section: two-column layout with property image on left
 * and property_long_description + CTA on right.
 * Matches the Stratman wp-block-stratman-content-columns bg-secondary pattern.
 */
export function PropertyAbout() {
  const property = useProperty();

  const basePath = getPropertyBasePath(property);
  const welcomePrefix = useContentToken('propertyabout.welcome_prefix');
  const name = useContentToken('propertyabout.property_name') || property.property_name || '';
  const luxurySuffix = useContentToken('propertyabout.luxury_suffix');
  const city = (useContentToken('propertyabout.city') || property.property_address_city || '').trim();
  const state = (useContentToken('propertyabout.state') || property.property_address_state || '').trim();
  const displayName = name.replace(/\s+Apartments$/i, '') || name;
  const image = useContentToken('propertyabout.image') || property.property_featured_image;
  const ctaText = useContentToken('propertyabout.cta_text');
  const ctaUrl = useContentToken('propertyabout.cta_url');
  const ctaEvent = useContentToken('propertyabout.cta_event');

  return (
    <section className="prop-content-section prop-content-section-dark">
        <div className="prop-content-grid">
          <div className="prop-content-img-col">
            <div style={{ position: 'relative' }}>
              <img src={cornerIcon} alt="" className="prop-corner-icon prop-corner-icon--tl" aria-hidden="true" />
              <div className="prop-content-img">
                <EditableToken path="propertyabout.image" type="image" label="Property Image" defaultField={contentSchema.tokens['propertyabout.image'].defaultField} as="div">
                  {image && (
                    <VibesitesImage url={image} alt={`${name} sign`} width={330} height={250} sizes="(min-width: 992px) 50vw, 100vw" />
                  )}
                </EditableToken>
              </div>
            </div>
          </div>
          <div className="prop-text-box">
            <h1>
              <EditableToken path="propertyabout.welcome_prefix" type="text" label="Welcome Prefix" defaultValue={contentSchema.tokens['propertyabout.welcome_prefix'].defaultValue} as="span">{welcomePrefix}</EditableToken>
              {' '}
              <EditableToken path="propertyabout.property_name" type="text" label="Property Name" defaultField={contentSchema.tokens['propertyabout.property_name'].defaultField} as="span">{displayName}</EditableToken>
              {' '}
              <EditableToken path="propertyabout.luxury_suffix" type="text" label="Luxury Suffix" defaultValue={contentSchema.tokens['propertyabout.luxury_suffix'].defaultValue} as="span">{luxurySuffix}</EditableToken>
              {' in '}
              <EditableToken path="propertyabout.city" type="text" label="City" defaultField={contentSchema.tokens['propertyabout.city'].defaultField} as="span">{city}</EditableToken>
              {state ? <>{', '}<EditableToken path="propertyabout.state" type="text" label="State" defaultField={contentSchema.tokens['propertyabout.state'].defaultField} as="span">{state}</EditableToken></> : ''}
            </h1>
            <RichTextContent
              path="propertyabout.description"
              label="Description"
              defaultField={contentSchema.tokens['propertyabout.description'].defaultField}
              as="div"
            />
            <EditableToken path="propertyabout.cta_url" type="url" label="CTA URL" defaultValue={contentSchema.tokens['propertyabout.cta_url'].defaultValue} as="span" style={{ display: 'contents' }}>
              <EditableToken path="propertyabout.cta_text" type="text" label="CTA Button Text" defaultValue={contentSchema.tokens['propertyabout.cta_text'].defaultValue} as="span" style={{ display: 'contents' }}>
                {ctaUrl ? (
                  <a className="prop-btn prop-floorplans-cta" href={ctaUrl} data-vibesite-event={ctaEvent || undefined}>{ctaText}</a>
                ) : (
                  <button type="button" className="prop-btn prop-floorplans-cta" onClick={openTourScheduler} data-vibesite-event={ctaEvent || 'schedule_tour_clicked'}>{ctaText}</button>
                )}
              </EditableToken>
            </EditableToken>
          </div>
        </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'PropertyAbout',
  displayName: 'Property About',
  tokens: {
    'propertyabout.welcome_prefix': {
      label: 'Welcome Prefix',
      type: 'text',
      defaultValue: 'Welcome to',
    },
    'propertyabout.property_name': {
      label: 'Property Name',
      type: 'text',
      defaultField: 'property_name',
    },
    'propertyabout.city': {
      label: 'City',
      type: 'text',
      defaultField: 'property_address_city',
    },
    'propertyabout.state': {
      label: 'State',
      type: 'text',
      defaultField: 'property_address_state',
    },
    'propertyabout.luxury_suffix': {
      label: 'Luxury Suffix',
      type: 'text',
      defaultValue: 'Luxury Apartments',
    },
    'propertyabout.image': {
      label: 'Property Image',
      type: 'image',
      defaultField: 'property_featured_image',
    },
    'propertyabout.description': {
      label: 'Description',
      type: 'richtext',
      defaultField: 'property_long_description',
    },
    'propertyabout.cta_text': {
      label: 'CTA Button Text',
      type: 'text',
      defaultValue: 'Schedule a Tour',
    },
    'propertyabout.cta_url': {
      label: 'CTA URL',
      type: 'url',
      defaultValue: '',
    },
    'propertyabout.cta_event': {
      label: 'CTA Tracked Event',
      type: 'event',
      defaultValue: 'schedule_tour_clicked',
      description: 'Canonical event fired when the CTA is clicked. Set to "None" to disable tracking.',
    },
  },
};

(PropertyAbout as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default PropertyAbout;
